import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin, addReactError } from '@datadog/browser-rum-react';
import packageJson from '../../package.json';
import { scheduleWhenIdle } from '@/lib/perf/schedule-idle';

let isInitialized = false;

export function initDatadogRum() {
  const isRumEnabled = import.meta.env.VITE_DD_ENABLED === 'true';

  if (!isRumEnabled) {
    return;
  }

  try {
    datadogRum.init({
      applicationId: import.meta.env.VITE_DD_APP_ID,
      clientToken: import.meta.env.VITE_DD_CLIENT_TOKEN,
      site: 'us3.datadoghq.com',
      service: import.meta.env.VITE_DD_SERVICE,
      env: import.meta.env.VITE_DD_ENV,
      version: packageJson.version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      defaultPrivacyLevel: 'mask-user-input',
      plugins: [reactPlugin({ router: false })],
    });

    isInitialized = true;
  } catch (error) {
    console.error('❌ Erro ao inicializar Datadog RUM:', error);
  }
}

function canTrack(): boolean {
  return isInitialized;
}

function startSessionReplayRecording(): void {
  if (!canTrack()) return;

  try {
    datadogRum.startSessionReplayRecording();
  } catch (error) {
    console.error('❌ Erro ao iniciar Session Replay do Datadog:', error);
  }
}

// Carrega depois do window.load, para não competir por rede com o boot crítico do shell.
export function scheduleSessionReplayRecording(): void {
  scheduleWhenIdle(startSessionReplayRecording);
}

export function trackEvent(
  eventName: string,
  attributes?: Record<string, unknown>
) {
  if (!canTrack()) return;

  datadogRum.addAction(eventName, {
    category: 'app',
    ...attributes,
  });
}

export function trackError(error: Error, context?: Record<string, unknown>) {
  if (!canTrack()) return;

  datadogRum.addError(error, {
    category: 'app',
    ...context,
  });
}

export function trackPerformance(
  measureName: string,
  duration: number,
  attributes?: Record<string, unknown>
) {
  if (!canTrack()) return;

  datadogRum.addTiming(measureName, duration);
  datadogRum.addAction('performance', {
    category: 'app',
    measure_name: measureName,
    duration,
    ...attributes,
  });
}

export function trackUserAction(
  actionName: string,
  attributes?: Record<string, unknown>
) {
  if (!canTrack()) return;

  datadogRum.addAction(actionName, {
    category: 'app',
    type: 'user_action',
    ...attributes,
  });
}

export function trackNavigation(
  route: string,
  attributes?: Record<string, unknown>
) {
  if (!canTrack()) return;

  datadogRum.addAction('navigation', {
    category: 'app',
    route,
    ...attributes,
  });
}

export function trackAPI(
  apiName: string,
  status: 'success' | 'error',
  duration?: number,
  attributes?: Record<string, unknown>
) {
  if (!canTrack()) return;

  datadogRum.addAction('api_call', {
    category: 'app',
    api: apiName,
    status,
    duration,
    ...attributes,
  });
}

export function identifyUser(userId: string, context: Record<string, unknown>) {
  if (!canTrack() || !userId) return;

  const currentUser = datadogRum.getUser?.() || {};

  datadogRum.setUser({
    id: userId,
    ...currentUser,
    ...context,
  });
}

export function trackReactError(
  error: Error,
  componentStack?: string,
  attributes?: Record<string, unknown>
) {
  if (!canTrack()) return;

  addReactError(error, {
    componentStack,
    ...attributes,
  });

  datadogRum.addAction('react_error', {
    category: 'app',
    error_message: error.message,
    componentStack,
    ...attributes,
  });
}

export { addReactError };
